import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import type { VFC } from 'react';

import Button, { ButtonGroup, TooltipButton } from '../generic/Button';
import { ErrorAlert } from '../generic/ErrorAlert';
import FilterCardRadios from '../generic/Filter/FilterCardRadios';
import { TextArea, FormGroup } from '../generic/Forms';
import Text from '../generic/Text';
import { useInputValue } from '../../hooks';
import { selectOptionFactory } from '../../utils';
import { CveDisputeReasons } from '../../../shared/cveDisputeTypes';
import type { CveDataDisputeReasonsKey, CveDataDisputeReasonsType } from '../../../shared/cveDisputeTypes';
import type { AsyncStatus } from '../../types';

type ModalReportVulnProps = {
  show: boolean;
  onHide: () => void;
  onSubmit: (note: string, reason: CveDataDisputeReasonsType) => Promise<void>;
  //  TODO: optional for now only, but should be made non-optional when <VulnerabilityDetailsPanel /> is removed after IssueDrawer project is GA.
  asyncStatus?: AsyncStatus;
};

const [defaultDisputeReason] = Object.keys(CveDisputeReasons) as [CveDataDisputeReasonsKey];

const disputeReasonOptions = Object.entries(CveDisputeReasons)
  .map(([value, label]) => selectOptionFactory<CveDataDisputeReasonsKey>(value as CveDataDisputeReasonsKey, label));

/**
 * Displays the vulnerability feedback modal, which
 * consists of some radio buttons to tell use which type of dispute this is,
 * as well as a textarea where the user should include dispute notes.
 */
const ModalReportVuln: VFC<ModalReportVulnProps> = ({
  show,
  onHide,
  onSubmit,
  asyncStatus,
}) => {
  const [reason, setReason] = useState<CveDataDisputeReasonsKey>(defaultDisputeReason);
  const [note, onNoteChange] = useInputValue('');

  const handleSubmit = async () => {
    if (asyncStatus?.isLoading) {
      return;
    }
    try {
      await onSubmit(note, CveDisputeReasons[reason]);
      //  Reset the modal.
      onNoteChange('');
      setReason(defaultDisputeReason);
      onHide();
    } catch {} // Errors are handled by Redux.
  };

  const hasNote = note?.length > 0;
  const shouldDisableSubmit = !hasNote || asyncStatus?.isLoading;

  return (
    <Modal
      show={show}
      onHide={onHide}
    >
      {/*
        Padding left ensures that the title aligns with the body
        Padding bottom removes an awkward space between the heading and the body.
      */}
      <Modal.Header style={{ paddingLeft: 40, paddingBottom: 0 }}>
        <Text.H2>
          Vulnerability Feedback
        </Text.H2>
      </Modal.Header>
      <Modal.Body>
        {asyncStatus && asyncStatus?.errors?.length > 0 && (
          <ErrorAlert status={asyncStatus} />
        )}
        <p className="margin-bottom-sm">
          Your feedback will be reviewed by the FOSSA team to help make our vulnerability experience even better.
        </p>
        <FormGroup>
          <FilterCardRadios
            label="What is the problem with this vulnerability?"
            options={disputeReasonOptions}
            value={reason}
            onChange={setReason}
            isDisabled={asyncStatus?.isLoading}
          />
          <TextArea
            label="Dispute note"
            placeholder="Let us know whats wrong with this vulnerability."
            value={note}
            onChange={onNoteChange}
            isDisabled={asyncStatus?.isLoading}
            isRequired
          />
        </FormGroup>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup direction="right">
          <Button
            type="secondary"
            size="medium"
            onClick={onHide}
            isDisabled={asyncStatus?.isLoading}
          >
            Cancel
          </Button>
          <TooltipButton
            id="vuln-dispute-modal-submit-button"
            type="primary"
            size="medium"
            onClick={handleSubmit}
            tooltip="You must include a note explaining your issue"
            isDisabled={shouldDisableSubmit}
            showTooltip={!hasNote}
          >
            Submit Feedback
          </TooltipButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalReportVuln;
